import moment from "moment"
import React, { memo, useCallback, useMemo, useState } from "react"
import { VariableSizeList as List, areEqual } from "react-window"
import memoize from "memoize-one"

import Stages from "./Stages"
import ProductionDayGrid from "./ProductionDayGrid"

import emptyIcon from "../../assets/images/schedule/production-warning.svg"
import {
    generateScheduleDateRange,
    getTotalSlotsForDay,
} from "./ProductionSchedule.utils"
import { useProductionScheduleContext } from "./ProductionScheduleContext"
import ProductionScheduleSkeleton from "./ProductionScheduleSkeleton"
import InspectionNoteModal from "./Inspection/InspectionNoteModal"
import InventoryWarningModal from "./InventoryWarningModal"
import InventoriesUsedModal from "./InventoriesUsedModal"

const BASE_COLUMN_WIDTH = 1
const SLOT_WIDTH = 100
const DEFAULT_SLOTS_PER_DAY = 4

const createItemData = memoize(
    (
        scheduleDates,
        isOverrideDate,
        getSlotsByStageForDate,
        getSortedSlotsByStageForDate,
        onSlotClick
    ) => ({
        scheduleDates,
        isOverrideDate,
        getSlotsByStageForDate,
        getSortedSlotsByStageForDate,
        onSlotClick,
    })
)
export const SLOT_MODAL_TYPE = {
    WARNING: "warning",
    INSPECTION: "inspection",
}

const calculateDayColumnWidth = totalSlots => {
    return BASE_COLUMN_WIDTH + totalSlots * SLOT_WIDTH
}

const DayColumnComponent = ({ index, style, data }) => {
    const {
        scheduleDates,
        isOverrideDate,
        getSlotsByStageForDate,
        getSortedSlotsByStageForDate,
        onSlotClick,
    } = data

    const item = scheduleDates[index]

    return (
        <div style={style} key={index} className="production-day">
            <ProductionDayGrid
                isOverride={isOverrideDate(item.date)}
                slotsByStage={getSlotsByStageForDate(item.date)}
                sortedSlotsByStage={getSortedSlotsByStageForDate(item.date)}
                onSlotClick={onSlotClick}
                {...item}
            />
        </div>
    )
}

const DayColumn = memo(DayColumnComponent, areEqual)
DayColumn.displayName = "DayColumn"

const ProductionScheduleCalendar = memo(props => {
    const {
        isLoading = false,
        dateRange = [],
        productionDetailsData = {},
        timeLineData = [],
        showWarningModal,
        setShowWarningModal = () => {},
        productionLineId,
    } = props

    const [activeModal, setActiveModal] = useState(null)
    const [selectedSlot, setSelectedSlot] = useState(null)

    const onSlotClick = (e, slot, type) => {
        if (!slot) return
        setSelectedSlot(slot)
        setActiveModal(type)
    }

    const closeModal = () => {
        setSelectedSlot(null)
        setActiveModal(null)
    }

    const {
        dailyConfigurationsMap,
        isOverrideDate,
        timeLineHashMap,
        loading,
        overridesHashmap,
        stageSequenceIds,
        warningHashMap,
        changeTimeLineData = () => {},
    } = useProductionScheduleContext()

    const scheduleDates = useMemo(
        () =>
            generateScheduleDateRange({
                startDate:
                    dateRange?.[0]?.startDate ||
                    productionDetailsData?.startDate,
                endDate: dateRange?.[0]?.endDate,
                timeLineData,
                dailyConfigurationsMap,
                overridesHashmap,
            }),
        [
            productionDetailsData?.startDate,
            dateRange,
            timeLineData,
            dailyConfigurationsMap,
            overridesHashmap,
        ]
    )

    const getTotalSlotsForDayCallback = useCallback(
        (dayOfWeek, date = null) => {
            return getTotalSlotsForDay(
                dayOfWeek,
                date,
                dailyConfigurationsMap,
                overridesHashmap,
                DEFAULT_SLOTS_PER_DAY
            )
        },
        [dailyConfigurationsMap, overridesHashmap]
    )

    const dateStageSlotCache = useMemo(() => {
        const cache = new Map()

        if (timeLineHashMap) {
            timeLineHashMap?.forEach((slots, stageId) => {
                slots.forEach(slot => {
                    const dateKey = moment(slot.date).format("YYYY-MM-DD")
                    const dayOfWeek = moment(slot.date).format("dddd")
                    const totalSlots = getTotalSlotsForDayCallback(
                        dayOfWeek,
                        slot.date
                    )

                    if (!cache.has(dateKey)) {
                        cache.set(dateKey, new Map())
                    }

                    if (!cache.get(dateKey).has(stageId)) {
                        cache
                            .get(dateKey)
                            .set(stageId, new Array(totalSlots).fill(null))
                    }

                    const stageSlots = cache.get(dateKey).get(stageId)
                    if (
                        slot.slotSequence >= 1 &&
                        slot.slotSequence <= totalSlots
                    ) {
                        stageSlots[slot.slotSequence - 1] = slot
                    }
                })
            })
        }

        return cache
    }, [timeLineHashMap, getTotalSlotsForDayCallback])

    const getSlotsByStageForDate = useCallback(
        date => {
            const result = {}
            const dateKey = moment(date).format("YYYY-MM-DD")
            const dayOfWeek = moment(date).format("dddd")
            const totalSlots = getTotalSlotsForDayCallback(dayOfWeek, date)
            const stageIds = productionDetailsData?.stages?.map(
                stage => stage.id
            )

            stageIds.forEach(stageId => {
                if (
                    dateStageSlotCache.has(dateKey) &&
                    dateStageSlotCache.get(dateKey).has(stageId)
                ) {
                    const slots = dateStageSlotCache.get(dateKey).get(stageId)
                    result[stageId] = slots.map(slot => {
                        if (!slot) return null
                        const warning = warningHashMap?.get(slot.id)
                        return {
                            ...slot,
                            warning: warning || null,
                        }
                    })
                } else {
                    result[stageId] = new Array(totalSlots).fill(null)
                }
            })

            return result
        },
        [
            dateStageSlotCache,
            timeLineHashMap,
            warningHashMap,
            getTotalSlotsForDayCallback,
        ]
    )

    const getSortedSlotsByStageForDate = useCallback(
        date => {
            const slotsByStage = getSlotsByStageForDate(date)
            return Object.entries(slotsByStage).sort(([a], [b]) => {
                const seqA = stageSequenceIds.get(Number(a)) || 0
                const seqB = stageSequenceIds.get(Number(b)) || 0
                return seqA - seqB
            })
        },
        [getSlotsByStageForDate, stageSequenceIds]
    )

    const itemCount = scheduleDates.length
    const itemData = createItemData(
        scheduleDates,
        isOverrideDate,
        getSlotsByStageForDate,
        getSortedSlotsByStageForDate,
        onSlotClick
    )

    const dayColumnWidths = useMemo(() => {
        return scheduleDates?.map(item => {
            const dayOfWeek = moment(item.date).format("dddd")
            const totalSlots = getTotalSlotsForDayCallback(dayOfWeek, item.date)
            return calculateDayColumnWidth(totalSlots)
        })
    }, [scheduleDates, getTotalSlotsForDayCallback])

    const getItemSize = index =>
        dayColumnWidths[index] || calculateDayColumnWidth(DEFAULT_SLOTS_PER_DAY)

    const totalWidth = useMemo(() => {
        return dayColumnWidths.reduce((sum, width) => sum + width, 0)
    }, [dayColumnWidths])

    if (isLoading || loading) {
        return <ProductionScheduleSkeleton />
    }

    if (
        Object.keys(productionDetailsData)?.length === 0 ||
        productionDetailsData?.stages?.length === 0
    ) {
        return (
            <div
                className="d-flex flex-column justify-content-center align-items-center py-5"
                style={{ height: "60vh" }}
            >
                <img src={emptyIcon} alt="No Data" width={100} height={100} />
                <p className="text-center mt-3">
                    {productionDetailsData?.stages?.length === 0
                        ? "No Data Available"
                        : "No Production Line Selected"}
                </p>
            </div>
        )
    }

    return (
        <div className="production-schedule">
            <Stages stagesData={productionDetailsData?.stages ?? []} />
            <div style={{ display: "flex", overflowX: "auto" }}>
                <List
                    width={Math.min(totalWidth, window.innerWidth - 100)}
                    itemCount={itemCount}
                    itemSize={getItemSize}
                    layout="horizontal"
                    itemData={itemData}
                    style={{ overflowY: "hidden", pointerEvents: "auto" }}
                >
                    {DayColumn}
                </List>
            </div>
            {activeModal === SLOT_MODAL_TYPE.INSPECTION && (
                <InspectionNoteModal
                    isOpen={activeModal === SLOT_MODAL_TYPE.INSPECTION}
                    toggle={closeModal}
                    handleSave={closeModal}
                    slot={selectedSlot}
                    setSelectedSlot={setSelectedSlot}
                />
            )}

            {showWarningModal && (
                <InventoryWarningModal
                    isOpen={showWarningModal}
                    toggleFunction={setShowWarningModal}
                    productionLineId={productionLineId}
                />
            )}

            {activeModal === SLOT_MODAL_TYPE.WARNING && (
                <InventoriesUsedModal
                    isOpen={activeModal === SLOT_MODAL_TYPE.WARNING}
                    handleCloseModal={closeModal}
                    onCancelClick={closeModal}
                    slot={selectedSlot}
                />
            )}
        </div>
    )
})

// Set display name for the memo component to fix the ESLint error
ProductionScheduleCalendar.displayName = "ProductionScheduleCalendar"

export default ProductionScheduleCalendar
